import React from 'react';
import { Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import useGeneralLedgerAccountForm from './hooks/useGeneralLedgerAccountForm';
import FormPartial from '../../components/form/formContainer/FormPartial';
import TextInput from '../../components/form/formInputs/TextInput/TextInput';
import SelectInput from '../../components/form/formInputs/SelectInput/SelectInput';
import { AgGridColumn } from 'ag-grid-react';
import BaseForm, { ParamTypes } from '../../components/form/baseForm/BaseForm';
import BaseGrid from '../../components/grids/BaseGrid';
import DefaultColumnTypes from '../../components/grids/columns/Column.constants';
import { RoutingValues } from '../../types/Shared.types';
import { GENERAL_LEDGER_ACCOUNT_FIELDS } from '../../constants/revisionHistory/revisionHistory.constants';

const GeneralLedgerAccountForm = () => {
    const { id, type } = useParams<ParamTypes>();
    const {
        fields,
        formMethods,
        accountStatusOptions,
        balanceTypeOptions,
        accountTypeOptions,
        onCreate,
        onUpdate,
        onDelete,
        closeForm,
        handleFieldChange,
        isLoadingAccount,
        activeAccount,
        hasFunctions,
        handleAccountTypeChange,
        canCreateChartOfAccount,
        canUpdateChartOfAccount,
        canDeleteChartOfAccount,
    } = useGeneralLedgerAccountForm(id);

    return (
        <BaseForm
            title={'General Ledger Account'}
            formMethods={formMethods}
            isLoading={isLoadingAccount}
            isNew={type === RoutingValues.create}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onCancel={closeForm}
            showOnRevision={true}
            fieldMappings={GENERAL_LEDGER_ACCOUNT_FIELDS}
            entityUuid={fields?.uuid}
            displayCreateButton={canCreateChartOfAccount}
            displayUpdateButton={canUpdateChartOfAccount}
            displayDeleteButton={canDeleteChartOfAccount}>
            <FormPartial>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextInput
                            fullWidth
                            label="Number"
                            id="number"
                            value={fields.number}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            maxLength={30}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextInput
                            fullWidth
                            label="Name"
                            id="name"
                            value={fields.name}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            required={true}
                            maxLength={90}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextInput
                            fullWidth
                            label="Description"
                            id="description"
                            value={fields.description}
                            onChange={handleFieldChange}
                            formMethods={formMethods}
                            maxLength={255}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInput
                            fullWidth
                            label="Status"
                            id="accountStatusId"
                            value={fields.accountStatusId}
                            onChange={handleFieldChange}
                            options={accountStatusOptions}
                            formMethods={formMethods}
                            required={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInput
                            fullWidth
                            label="Account Type"
                            id="accountTypeId"
                            value={fields.accountTypeId}
                            onChange={handleAccountTypeChange}
                            options={accountTypeOptions}
                            formMethods={formMethods}
                            required={true}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInput
                            fullWidth
                            label="Balance Type"
                            id="balanceTypeId"
                            value={fields.balanceTypeId}
                            onChange={handleFieldChange}
                            options={balanceTypeOptions}
                            formMethods={formMethods}
                            required={true}
                        />
                    </Grid>
                </Grid>
            </FormPartial>
            <FormPartial title="Account Functions" isVisible={hasFunctions()}>
                <BaseGrid
                    rowData={activeAccount?.functions || []}
                    displayGrid={true}
                    defaultColDef={{
                        resizable: true,
                        wrapText: true,
                    }}>
                    <AgGridColumn
                        field="functionCode.code"
                        headerName="Function Code"
                        {...DefaultColumnTypes.ShortText}
                    />
                    <AgGridColumn
                        field="functionCode.description"
                        headerName="Description"
                        {...DefaultColumnTypes.LongText}
                        maxWidth={1000}
                    />
                </BaseGrid>
            </FormPartial>
        </BaseForm>
    );
};

export default GeneralLedgerAccountForm;
